import { defineStore } from 'pinia';
import { Router } from 'vue-router';
import * as api from '@/api';
import router, { constantRoutes } from '@/router';
import preRoutes from '@/router/preRoutes';
import { getTreeNodeInfoByKey } from '@/utils/tools';

/** 防止路由名称重复 */
let routeIndex = 0;

// 通过path获取节点信息
export const getTreeNodeInfoByPath = (path, treeData) => {
  return getTreeNodeInfoByKey('path', path, treeData);
};

export type TreeNodeInfo = {
  menuName?: string;
  path: string;
  redirect?: string;
  notMenu?: boolean;
  meta: Record<string, string>;
  children?: Array<TreeNodeInfo>;
};

function getDefaultRedirectPath(routes) {
  let _path = '/404';
  for (let i = 0; i < routes.length; i++) {
    const _route = routes[i];
    if (_route.path && _route.path !== '/') {
      _path = _route.path;
      break;
    }
  }
  return _path;
}

function getDefaultRoute(routes, routerObj?: Router) {
  // 先删除已存在的defaultRoute路由
  try {
    routerObj ? routerObj.removeRoute('defaultRoute') : router.removeRoute('defaultRoute');
  } catch (error) {
    // 路由不存在时会报错，忽略此错误
    console.log('旧的defaultRoute路由不存在，无需删除');
  }
  const defaultPath = getDefaultRedirectPath(routes);
  const defaultRoute = {
    path: '/',
    hidden: true,
    name: 'defaultRoute',
    redirect: defaultPath,
  };
  routerObj ? routerObj.addRoute(defaultRoute) : router.addRoute(defaultRoute);
  return defaultRoute;
}
export const useMenuStore = defineStore('useMenuStore', {
  state: () => ({
    routes: [],
    authRoutes: [],
    activePath: '',
    menus: [],
  }),
  persist: true,
  actions: {
    clearSidebarList() {
      return new Promise<void>(resolve => {
        localStorage.removeItem('sidebarList');
        resolve();
      });
    },
    generateTreeDataRoutes(list, routerObj?: Router): Array<any> {
      const result: Array<unknown> = [];
      list.forEach(async item => {
        const funItem: TreeNodeInfo = getTreeNodeInfoByPath(item.path, preRoutes);
        let _routeItem: any = null;
        if (funItem) {
          const relateChildRoutes: Array<any> = [];
          const redirectPath = funItem.redirect;
          if (funItem.children) {
            // 判断预定义里的路由的父级path能否匹配到菜单
            // console.log(item.path, funItem.children);
            const passedChildrenPath: Array<string> = [];
            funItem.children.forEach(ii => {
              if (getTreeNodeInfoByPath(ii.path, list)) {
                // 判断路由是否在菜单列表里
                passedChildrenPath.push(ii.path);
              } else if (ii.notMenu) {
                // 通过notMenu标识判断是非菜单页面，否则可能出现菜单页没权限但是命中了下面的规则，而被注册进路由的问题
                // 不再菜单列表中，则判断父级path是否在菜单列表中
                const splt = ii.path.split('/');
                splt.pop(); // 舍弃最后一级
                const parentPath = splt.join('/');
                if (getTreeNodeInfoByPath(parentPath, list)) {
                  relateChildRoutes.push({
                    ...ii,
                    hidden: true,
                    name: ii.menuName,
                    meta: {
                      ...ii.meta,
                      title: ii.menuName,
                      parentPath,
                    },
                  });
                }
              }
            });

            if (redirectPath && !passedChildrenPath.includes(redirectPath)) {
              funItem.redirect = passedChildrenPath[0] || undefined;
            }
          }
          _routeItem = {
            ...funItem,
            name: item.menuName + routeIndex++,
            meta: {
              ...funItem.meta,
              icon: item.icon ?? funItem?.meta?.icon,
              title: item.menuName,
              isMenu: true,
            },
            children: item.children?.length
              ? [...this.generateTreeDataRoutes(item.children, routerObj), ...relateChildRoutes]
              : [...relateChildRoutes],
          };
          result.push(_routeItem);
        } else if (item.isIframe) {
          _routeItem = {
            name: item.menuName + routeIndex++,
            path: item.path,
            meta: {
              icon: item.icon,
              title: item.menuName,
              isMenu: true,
              iframeUrl: item.iframeUrl,
              isIframe: true,
            },
            component: () => import('@/views/iframe/index.vue'),
          };
          // 匹配iframe
          result.push(_routeItem);
        }
        // debugger;
        routerObj ? routerObj.addRoute(_routeItem) : router.addRoute(_routeItem);
      });

      return result;
    },
    setActivePath(activePath) {
      this.activePath = activePath;
    },
    getSidebarList(compId = '') {
      return new Promise<boolean>(async resolve => {
        let resp;
        let noAuth = false;
        try {
          // 获取侧边栏
          resp = await api.userManageApi.getSidebars({ productCode: 'emcomp-back-office', compId });
        } catch (e) {
          noAuth = true;
        }
        const list = resp || [];
        await this.generateRoutes(list);
        localStorage.setItem('routes', JSON.stringify(list));
        resolve(noAuth);
      });
    },
    // 设置菜单
    generateRoutes(routes, routerObj?: Router) {
      return new Promise<void>(resolve => {
        // 查找匹配的路由
        const authRoutes = this.generateTreeDataRoutes(routes, routerObj);
        const addRoutes = [getDefaultRoute(routes, routerObj), ...authRoutes];
        this.routes = [...constantRoutes, ...addRoutes];
        this.authRoutes = authRoutes;
        this.menus = authRoutes[0]?.children ?? [];
        console.log('生成的路由:', router.getRoutes());
        resolve();
      });
    },
    // 清空路由
    clearRoutes({ commit }) {
      return new Promise<void>(resolve => {
        commit('clear_routes');
        resolve();
      });
    },
  },
});
